import React, {useEffect, useRef, useState} from 'react';

import {LocationSuggestion, PlacesServiceImpl} from '../../services/places/places.service.impl';

const placesService = new PlacesServiceImpl();

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputClassName?: string;
}

// Free-text location input backed by Google Places (cities) suggestions from /places/autocomplete.
// Typing fetches debounced suggestions; picking one fills the field. Free text is still allowed.
export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({value, onChange, placeholder, inputClassName = ''}) => {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    // Don't re-query right after the user picked a suggestion.
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }

    let active = true;
    const handle = setTimeout(() => {
      placesService.autocompleteLocations(trimmed).then(results => {
        if (!active) return;
        setSuggestions(results);
        setOpen(results.length > 0);
      });
    }, 250);

    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [value]);

  const handleSelect = (description: string) => {
    skipNextFetchRef.current = true;
    onChange(description);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        className={inputClassName}
        onChange={event => onChange(event.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        value={value}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {suggestions.map(suggestion => (
            <li key={suggestion.placeId || suggestion.description}>
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => handleSelect(suggestion.description)}
                type="button">
                <svg className="h-4 w-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="truncate">{suggestion.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationAutocomplete;
